import { useParams, Link } from "react-router-dom";
import SEOHead from "@/components/feature/SEOHead";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { articles } from "@/mocks/articles";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen pt-24 flex flex-col items-center justify-center gap-4">
        <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-file-unknow-line text-2xl"></i>
        </div>
        <p className="text-foreground-600 text-sm">文章未找到</p>
        <Link
          to="/"
          className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors no-underline whitespace-nowrap cursor-pointer"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const siteUrl = typeof window !== "undefined" ? window.location.origin + __BASE_PATH__ : "";
  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.slug !== article.slug)
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-background-50">
      <SEOHead
        title={`${article.title} | K-Stream 解析`}
        description={article.excerpt}
        keywords={article.tags.join(",")}
        canonicalUrl={siteUrl + "/article/" + article.slug}
        ogImage={article.imageUrl}
        ogType="article"
        articleData={article}
        breadcrumbs={[
          { name: "首页", url: siteUrl + "/" },
          { name: article.category, url: siteUrl + "/category/" + article.categorySlug },
          { name: article.title, url: siteUrl + "/article/" + article.slug },
        ]}
      />

      {/* Header */}
      <section className="pt-28 pb-6 bg-background-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb currentTitle={article.title} />

          <div className="mb-4">
            <Link
              to={`/category/${article.categorySlug}`}
              className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-600 hover:bg-primary-200 transition-colors no-underline whitespace-nowrap cursor-pointer"
            >
              {article.category}
            </Link>
          </div>

          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
            {article.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line"></i>
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              {article.date}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line"></i>
              {article.readTime}
            </span>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="w-full h-64 md:h-96 rounded-lg overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-full object-cover"
              title={article.title}
            />
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-10 bg-background-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <article
            className="prose prose-base max-w-none text-foreground-800 leading-relaxed
              [&_h2]:font-heading [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-bold [&_h2]:text-foreground-950 [&_h2]:mt-10 [&_h2]:mb-4
              [&_p]:text-foreground-700 [&_p]:mb-5 [&_p]:leading-relaxed [&_p]:text-sm [&_p]:md:text-base
              [&_strong]:text-foreground-950"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Tags */}
          <div className="mt-10 pt-6 border-t border-background-200/70">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500 mr-1">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 rounded-full text-xs bg-background-100 text-foreground-600 border border-background-200/70"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Share */}
          <div className="mt-6 flex items-center gap-3">
            <span className="text-sm text-foreground-500">分享：</span>
            <a
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(siteUrl + "/article/" + article.slug)}`}
              target="_blank"
              rel="nofollow noopener"
              className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-foreground-950 hover:text-background-50 transition-colors cursor-pointer"
              aria-label="Share on Twitter"
            >
              <i className="ri-twitter-x-line text-sm"></i>
            </a>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(siteUrl + "/article/" + article.slug)}`}
              target="_blank"
              rel="nofollow noopener"
              className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-foreground-950 hover:text-background-50 transition-colors cursor-pointer"
              aria-label="Share on Facebook"
            >
              <i className="ri-facebook-line text-sm"></i>
            </a>
            <button
              onClick={() => {
                navigator.clipboard.writeText(siteUrl + "/article/" + article.slug);
              }}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-foreground-950 hover:text-background-50 transition-colors cursor-pointer"
              aria-label="Copy link"
            >
              <i className="ri-link text-sm"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8 text-center">
              相关文章
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.id}
                  to={`/article/${ra.slug}`}
                  onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                  className="group block bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-300 transition-all duration-300 no-underline cursor-pointer"
                >
                  <div className="relative w-full h-40 overflow-hidden">
                    <img
                      src={ra.imageUrl}
                      alt={ra.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      title={ra.title}
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-sm font-semibold text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors">
                      {ra.title}
                    </h3>
                    <p className="text-xs text-foreground-400 mt-2">{ra.date}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}